import type { Transition } from "@uirouter/core";

import categoryService from "app/common/services/categoryService";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { BasicDurations } from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import { V2_ROUTES } from "../routes/routes";
import { hasLobSegments } from "../routes/routeUtils";

const getSegmentCompatibleCategories = (categories: string[]): string[] => {
    if (categories.length < 2) {
        return categories;
    }

    const primaryHasSegments = !!categoryService.categoryQueryParamToCategoryObject(categories[0])?.segments?.length;

    return categories.filter((categoryParam) => {
        const categoryObject = categoryService.categoryQueryParamToCategoryObject(categoryParam);
        return !!categoryObject?.segments?.length === primaryHasSegments;
    });
};

export const onEnterValidateParams = (transition: Transition): void => {
    const toState = transition.to() as IRouterState;
    const params = transition.params();
    const categoryParam = params.category;
    const categories = Array.isArray(categoryParam) ? categoryParam : [categoryParam];

    const hasPreDefinedIndustry = categories.some(
        (category: string) => !UserCustomCategoryService.isCustomCategory(category),
    );
    const hasLobs = hasLobSegments(categories);
    const segmentCompatibleCategories = getSegmentCompatibleCategories(categories);
    const shouldRemoveSegmentIncompatibleCategories = segmentCompatibleCategories.length !== categories.length;

    const shouldResetIncludeSubdomains = hasPreDefinedIndustry && params.includeSubdomains === false;
    const shouldRemoveComparedDuration = hasLobs && params.comparedDuration;
    const shouldNormalizeMarketSharesTimeGranularity =
        toState.name === V2_ROUTES.players &&
        params.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS &&
        params.marketSharesTimeGranularity === "M";

    if (
        shouldResetIncludeSubdomains ||
        shouldRemoveComparedDuration ||
        shouldNormalizeMarketSharesTimeGranularity ||
        shouldRemoveSegmentIncompatibleCategories
    ) {
        const newParams = { ...params };

        if (shouldResetIncludeSubdomains) {
            newParams.includeSubdomains = true;
        }

        if (shouldRemoveComparedDuration) {
            newParams.comparedDuration = undefined;
        }

        if (shouldNormalizeMarketSharesTimeGranularity) {
            newParams.marketSharesTimeGranularity = "D";
        }

        if (shouldRemoveSegmentIncompatibleCategories) {
            newParams.category =
                segmentCompatibleCategories.length === 1 ? segmentCompatibleCategories[0] : segmentCompatibleCategories;
        }

        void proNavigator.go(toState.name, newParams, { location: "replace" });
    }
};
